import { Stores } from '@app/Constants/storage';

export default class Storage {
  constructor() {
    this.dbPromise = this.init();
  }

  init() {
    return new Promise((resolve, reject) => {
      const request = window.indexedDB.open('ocean-eye', 1);

      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        Stores.forEach((store) => {
          if (!db.objectStoreNames.contains(store.name)) {
            const objectStore = db.createObjectStore(store.name, {
              keyPath: 'id',
            });
            if (store.indexes.length) {
              store.indexes.forEach((index) => {
                objectStore.createIndex(index, index, { unique: false });
              });
            }
          }
        });
      };

      request.onsuccess = (event) => {
        resolve(event.target.result);
      };

      request.onerror = (event) => {
        reject(event.target.error);
      };
    });
  }

  async findFirst(storeName, params = {}) {
    let db = await this.dbPromise;
    if (db.readyState !== 'open') {
      db = await this.init();
    }

    // To find the data based on field name, use key and value
    // E.g. params: { key: 'id', value: 5 }
    const queryParams = {
      key: '',
      value: '',
      ...params,
    };

    const transaction = db.transaction(storeName, 'readonly');
    const store = transaction.objectStore(storeName);
    let request = store.openCursor();
    return new Promise((resolve, reject) => {
      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          if (queryParams.key && queryParams.value) {
            if (cursor.value[queryParams.key] === queryParams.value) {
              resolve(cursor.value);
            } else {
              cursor.continue();
            }
          } else {
            resolve(cursor.value);
          }
        }
      };
      request.onerror = (event) => reject(event.target.error);
    });
  }

  async find(storeName, params = {}) {
    let db = await this.dbPromise;
    if (db.readyState !== 'open') {
      db = await this.init();
    }
    const queryParams = {
      searchTerm: '',
      searchSubject: '',
      excludeIds: '',
      sortBy: '',
      sortOrder: '',
      includeIds: '',
      ...params,
    };
    const transaction = db.transaction(storeName, 'readonly');
    const store = transaction.objectStore(storeName);
    const index = queryParams.sortBy ? store.index(queryParams.sortBy) : store;
    return new Promise((resolve, reject) => {
      const request = index.openCursor(
        null,
        queryParams.sortOrder === 'desc' ? 'prev' : 'next'
      );
      const results = [];
      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          const value = cursor.value;
          const isSearchMatch = queryParams.searchTerm
            ? value[queryParams.searchSubject]
                .toLowerCase()
                .indexOf(queryParams.searchTerm.toLowerCase()) > -1
            : true;
          const isNotExcluded = !queryParams.excludeIds
            .split(', ')
            .some((excludeId) => Number(excludeId) === value.id);
          const isIncluded = queryParams.includeIds
            ? queryParams.includeIds
                .split(', ')
                .some((includeIds) => Number(includeIds) === value.id)
            : true;

          if (
            isSearchMatch &&
            isNotExcluded &&
            ((queryParams.limit && results.length < queryParams.limit) ||
              !queryParams.limit) &&
            isIncluded
          ) {
            results.push(value);
          }
          cursor.continue();
        } else {
          resolve(results);
        }
      };
      request.onerror = () => reject(request.error);
    });
  }

  async store(storeName, value) {
    let db = await this.dbPromise;
    if (db.readyState !== 'open') {
      db = await this.init();
    }
    const transaction = db.transaction(storeName, 'readwrite');
    const store = transaction.objectStore(storeName);
    store.put(value);
  }

  async delete(storeName, key) {
    let db = await this.dbPromise;
    if (db.readyState !== 'open') {
      db = await this.init();
    }
    const transaction = db.transaction(storeName, 'readwrite');
    const store = transaction.objectStore(storeName);
    store.delete(key);
  }

  async truncate(storeName) {
    let db = await this.dbPromise;
    if (db.readyState !== 'open') {
      db = await this.init();
    }
    const transaction = db.transaction(storeName, 'readwrite');
    const store = transaction.objectStore(storeName);
    store.clear();
  }
}
